import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import StatsSection from './components/StatsSection';
import PortfolioSection from './components/PortfolioSection';
import InteractiveMethodology from './components/InteractiveMethodology';
import UseCasesSection from './components/UseCasesSection';
import CartaCurationSection from './components/CartaCurationSection';
import IntentPathwaysSection from './components/IntentPathwaysSection';
import ComparativeSection from './components/ComparativeSection';

export default function HomePage() {
  const { t } = useTranslation('home');
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  const heroRef = useRef<HTMLDivElement>(null);

  // Parallax effect
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Set page title dynamically
    document.title = "FGT Capital São Paulo | Engenharia Patrimonial Consórcios Regulados";
  }, []);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.fade-on-scroll').forEach((el) => {
      observerRef.current?.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, []);

  // Auto-rotate hero images
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveImageIndex((prev) => (prev + 1) % premiumHeroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const whatsappUrl = `https://wa.me/5511930322408`;

  const premiumHeroImages = [
    {
      url: "https://public.readdy.ai/ai/img_res/84d381a3d9b355e5102716fe32ab44c5.jpg",
      title: "Residências de Alto Padrão"
    },
    {
      url: "https://readdy.ai/api/search-image?query=premium%20luxury%20sports%20car%20Porsche%20911%20GT3%20in%20metallic%20grey%20parked%20in%20modern%20minimalist%20garage%20with%20ambient%20lighting%2C%20architectural%20photography%20style%2C%20clean%20lines%20and%20sophisticated%20shadows%2C%20high-end%20automotive%20editorial%2C%20pristine%20condition%2C%20no%20people%2C%20luxurious%20atmosphere&width=1200&height=800&seq=fgt-hero-luxury-2&orientation=landscape",
      title: "Veículos de Alto Padrão"
    },
    {
      url: "https://readdy.ai/api/search-image?query=luxury%20yacht%20marina%20at%20golden%20hour%20with%20premium%20vessels%20worth%20millions%2C%20sophisticated%20nautical%20photography%2C%20warm%20sunset%20reflections%20on%20water%2C%20elegant%20boat%20details%20and%20polished%20surfaces%2C%20high-end%20lifestyle%20editorial%20style%2C%20no%20people%2C%20serene%20and%20exclusive%20atmosphere&width=1200&height=800&seq=fgt-hero-luxury-3&orientation=landscape",
      title: "Investimentos Náuticos"
    }
  ];

  const testimonials = t('home:testimonials.items', { returnObjects: true }) as Array<{
    quote: string;
    profile: string;
    tag: string;
    credit: string;
  }>;

  const tagColors: Record<string, string> = {
    'Aquisição Imobiliária': '#F97700',
    'Real Estate Acquisition': '#F97700',
    'Planejamento Patrimonial': '#2E7D32',
    'Wealth Planning': '#2E7D32',
    'Estruturação Empresarial': '#1565C0',
    'Corporate Structuring': '#1565C0',
    'Construção e Reforma': '#6D4C41',
    'Construction & Renovation': '#6D4C41',
    'Carta Contemplada': '#6A1B9A',
    'Contemplated Letter': '#6A1B9A',
  };

  return (
    <>
      <div className="min-h-screen bg-[#F6F2EC]">
        {/* Hero Section with Premium Image Carousel */}
        <section ref={heroRef} className="relative min-h-[90vh] flex items-center bg-[#F6F2EC] pt-20 overflow-hidden">
          {/* Background Images Carousel */}
          <div className="absolute inset-0 overflow-hidden">
            {premiumHeroImages.map((image, index) => (
              <div
                key={index}
                className={`absolute inset-0 transition-all duration-1000 ease-in-out ${
                  index === activeImageIndex ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
                }`}
                style={{
                  transform: `translateY(${scrollY * 0.3}px) scale(${index === activeImageIndex ? 1 : 1.05})`
                }}
              >
                <img
                  src={image.url}
                  alt={image.title}
                  className="absolute right-0 top-0 w-3/5 h-full object-cover opacity-25 mix-blend-multiply object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#F6F2EC] via-[#F6F2EC]/95 to-transparent"></div>
              </div>
            ))}
          </div>

          {/* Content */}
          <div className="max-w-[1440px] mx-auto px-8 lg:px-16 w-full relative z-10">
            <div className="max-w-[720px]">
              <p className="text-[11px] uppercase tracking-[0.08em] font-medium text-[#F97700] mb-6 animate-fade-in-up">
                {t('home:hero.subtitle')}
              </p>
              <h1 className="text-5xl lg:text-6xl font-semibold text-[#0B0B0B] leading-[1.1] tracking-tight mb-6 animate-fade-in-up delay-200">
                {t('home:hero.title')}
              </h1>
              <p className="text-lg text-[#2E2E2E] leading-relaxed max-w-[480px] mb-10 animate-fade-in-up delay-400">
                {t('home:hero.description')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up delay-600">
                <Link
                  to="/diagnostico"
                  className="px-8 py-4 bg-[#F97700] text-white text-base font-medium rounded hover:bg-[#E56E00] transition-all duration-300 hover:scale-[1.02] hover:shadow-lg text-center whitespace-nowrap cursor-pointer"
                >
                  {t('common:cta.primary')}
                </Link>
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-4 border-2 border-[#0B0B0B] text-[#0B0B0B] text-base font-medium rounded hover:bg-[#0B0B0B] hover:text-white transition-all duration-300 hover:shadow-lg text-center whitespace-nowrap cursor-pointer"
                >
                  {t('common:cta.secondary')}
                </a>
              </div>
            </div>
          </div>

          {/* Image Navigation Dots */}
          <div className="absolute bottom-20 right-8 flex flex-col gap-3 z-20">
            {premiumHeroImages.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveImageIndex(index)}
                className={`transition-all duration-300 cursor-pointer ${
                  index === activeImageIndex
                    ? 'w-3 h-8 bg-[#F97700] rounded-full'
                    : 'w-3 h-3 bg-white/60 hover:bg-white/80 rounded-full'
                }`}
              />
            ))}
          </div>

          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce z-20">
            <i className="ri-arrow-down-line text-3xl text-[#2E2E2E]"></i>
          </div>
        </section>

        {/* ── USE CASES: When a carta may make sense ── */}
        <UseCasesSection />

        {/* Manifesto Section */}
        <section className="bg-[#FBF8F3] py-40">
          <div className="max-w-[1440px] mx-auto px-8 lg:px-16">
            <div className="max-w-[720px] mx-auto text-center">
              <div className="w-0.5 h-20 bg-[#F97700] mx-auto mb-12"></div>
              <h2 className="text-4xl font-semibold text-[#0B0B0B] leading-[1.3] fade-on-scroll opacity-0">
                <span className="block mb-2">Patrimônio não se improvisa.</span>
                <span className="block mb-2">Se projeta.</span>
                <span className="block">Sem promessas. Com estrutura.</span>
              </h2>
            </div>
          </div>
        </section>

        {/* Premium Pillars Section with Hover Effects */}
        <section className="bg-[#F6F2EC] py-32">
          <div className="max-w-[1440px] mx-auto px-8 lg:px-16">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white border border-[#E7E1D9] rounded-lg p-12 hover:shadow-2xl hover:scale-[1.02] transition-all duration-500 fade-on-scroll opacity-0 relative group cursor-pointer overflow-hidden">
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#F97700]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <span className="absolute top-8 right-8 text-[80px] font-light text-[#FFF1E6] leading-none transition-all duration-500 group-hover:text-[#F97700]/10">01</span>
                <div className="w-12 h-12 flex items-center justify-center mb-6 transition-transform duration-500 group-hover:scale-110 relative z-10">
                  <i className="ri-compass-3-line text-5xl text-[#F97700]"></i>
                </div>
                <h3 className="text-2xl font-semibold text-[#0B0B0B] mb-4 relative z-10">
                  {t('home:pillars.engineering.title')}
                </h3>
                <p className="text-[15px] text-[#2E2E2E] leading-relaxed relative z-10">
                  {t('home:pillars.engineering.description')}
                </p>
              </div>

              <div className="bg-white border border-[#E7E1D9] rounded-lg p-12 hover:shadow-2xl hover:scale-[1.02] transition-all duration-500 fade-on-scroll opacity-0 relative group cursor-pointer overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#F97700]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <span className="absolute top-8 right-8 text-[80px] font-light text-[#FFF1E6] leading-none transition-all duration-500 group-hover:text-[#F97700]/10">02</span>
                <div className="w-12 h-12 flex items-center justify-center mb-6 transition-transform duration-500 group-hover:scale-110 relative z-10">
                  <i className="ri-shield-check-line text-5xl text-[#F97700]"></i>
                </div>
                <h3 className="text-2xl font-semibold text-[#0B0B0B] mb-4 relative z-10">
                  {t('home:pillars.governance.title')}
                </h3>
                <p className="text-[15px] text-[#2E2E2E] leading-relaxed relative z-10">
                  {t('home:pillars.governance.description')}
                </p>
              </div>

              <div className="bg-white border border-[#E7E1D9] rounded-lg p-12 hover:shadow-2xl hover:scale-[1.02] transition-all duration-500 fade-on-scroll opacity-0 relative group cursor-pointer overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#F97700]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <span className="absolute top-8 right-8 text-[80px] font-light text-[#FFF1E6] leading-none transition-all duration-500 group-hover:text-[#F97700]/10">03</span>
                <div className="w-12 h-12 flex items-center justify-center mb-6 transition-transform duration-500 group-hover:scale-110 relative z-10">
                  <i className="ri-settings-3-line text-5xl text-[#F97700]"></i>
                </div>
                <h3 className="text-2xl font-semibold text-[#0B0B0B] mb-4 relative z-10">
                  {t('home:pillars.execution.title')}
                </h3>
                <p className="text-[15px] text-[#2E2E2E] leading-relaxed relative z-10">
                  {t('home:pillars.execution.description')}
                </p>
              </div>
            </div>
          </div>
        </section>

        <StatsSection />

        {/* ── CARTA CURATION: Technical curation block ── */}
        <CartaCurationSection />

        <PortfolioSection />

        <InteractiveMethodology />

        {/* Premium Solutions Grid with Interactive Images */}
        <section className="bg-[#FBF8F3] py-32">
          <div className="max-w-[1440px] mx-auto px-8 lg:px-16">
            <h2 className="text-4xl font-semibold text-[#0B0B0B] text-center mb-16">
              {t('home:solutions.title')}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Real Estate Solution with Premium Property Image */}
              <Link
                to="/solucoes"
                className="bg-white border border-[#E7E1D9] rounded-lg overflow-hidden hover:border-[#F97700] transition-all duration-500 group cursor-pointer hover:shadow-2xl hover:scale-[1.02]"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=ultra%20luxury%20modern%20mansion%20exterior%20with%20infinity%20pool%20overlooking%20ocean%2C%20architectural%20masterpiece%20worth%20over%2010%20million%20dollars%2C%20pristine%20landscaping%20and%20premium%20materials%2C%20golden%20hour%20photography%20with%20warm%20lighting%2C%20editorial%20real%20estate%20style%2C%20no%20people%2C%20sophisticated%20composition&width=600&height=400&seq=fgt-real-estate-premium&orientation=landscape"
                    alt="Aquisição Imobiliária Premium"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent group-hover:from-black/30 transition-all duration-500"></div>
                </div>
                <div className="p-10">
                  <p className="text-[11px] uppercase tracking-[0.08em] font-medium text-[#F97700] mb-3">
                    {t('home:solutions.real_estate.category')}
                  </p>
                  <h3 className="text-3xl font-semibold text-[#0B0B0B] mb-4">
                    {t('home:solutions.real_estate.title')}
                  </h3>
                  <p className="text-[15px] text-[#2E2E2E] leading-relaxed mb-6">
                    {t('home:solutions.real_estate.description')}
                  </p>
                  <span className="text-sm font-medium text-[#0B0B0B] group-hover:text-[#F97700] transition-colors">
                    Saiba mais →
                  </span>
                </div>
              </Link>

              {/* Construction Solution with Premium Architecture */}
              <Link
                to="/solucoes"
                className="bg-white border border-[#E7E1D9] rounded-lg overflow-hidden hover:border-[#F97700] transition-all duration-500 group cursor-pointer hover:shadow-2xl hover:scale-[1.02]"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=luxury%20contemporary%20architecture%20construction%20site%20with%20premium%20materials%20and%20sophisticated%20design%20elements%2C%20high-end%20residential%20development%20in%20progress%2C%20clean%20modern%20lines%20and%20expensive%20finishes%20visible%2C%20professional%20construction%20photography%2C%20no%20people%2C%20elegant%20and%20refined%20atmosphere&width=600&height=400&seq=fgt-construction-premium&orientation=landscape"
                    alt="Construção Premium"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent group-hover:from-black/30 transition-all duration-500"></div>
                </div>
                <div className="p-10">
                  <p className="text-[11px] uppercase tracking-[0.08em] font-medium text-[#F97700] mb-3">
                    {t('home:solutions.construction.category')}
                  </p>
                  <h3 className="text-3xl font-semibold text-[#0B0B0B] mb-4">
                    {t('home:solutions.construction.title')}
                  </h3>
                  <p className="text-[15px] text-[#2E2E2E] leading-relaxed mb-6">
                    {t('home:solutions.construction.description')}
                  </p>
                  <span className="text-sm font-medium text-[#0B0B0B] group-hover:text-[#F97700] transition-colors">
                    Saiba mais →
                  </span>
                </div>
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Wealth Planning with Premium Portfolio */}
              <Link
                to="/solucoes"
                className="bg-white border border-[#E7E1D9] rounded-lg overflow-hidden hover:border-[#F97700] transition-all duration-500 group cursor-pointer hover:shadow-xl hover:scale-[1.02]"
              >
                <div className="relative h-32 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=luxury%20financial%20planning%20documents%20and%20premium%20investment%20portfolio%20on%20mahogany%20desk%2C%20sophisticated%20wealth%20management%20materials%20with%20gold%20pen%20and%20leather%20folder%2C%20high-end%20business%20photography%2C%20warm%20ambient%20lighting%2C%20elegant%20and%20professional%20composition%2C%20no%20people&width=400&height=200&seq=fgt-planning-premium&orientation=landscape"
                    alt="Planejamento Patrimonial"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent group-hover:from-black/30 transition-all duration-500"></div>
                </div>
                <div className="p-8">
                  <p className="text-[11px] uppercase tracking-[0.08em] font-medium text-[#F97700] mb-3">
                    {t('home:solutions.planning.category')}
                  </p>
                  <h3 className="text-xl font-semibold text-[#0B0B0B] mb-3">
                    {t('home:solutions.planning.title')}
                  </h3>
                  <p className="text-[14px] text-[#2E2E2E] leading-relaxed mb-4">
                    {t('home:solutions.planning.description')}
                  </p>
                  <span className="text-sm font-medium text-[#0B0B0B] group-hover:text-[#F97700] transition-colors">
                    Saiba mais →
                  </span>
                </div>
              </Link>

              {/* Corporate with Premium Office */}
              <Link
                to="/empresas"
                className="bg-white border border-[#E7E1D9] rounded-lg overflow-hidden hover:border-[#F97700] transition-all duration-500 group cursor-pointer hover:shadow-xl hover:scale-[1.02]"
              >
                <div className="relative h-32 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=luxury%20corporate%20boardroom%20with%20premium%20leather%20chairs%20and%20mahogany%20table%2C%20sophisticated%20business%20environment%20with%20city%20skyline%20view%2C%20high-end%20executive%20office%20interior%2C%20professional%20corporate%20photography%2C%20elegant%20lighting%2C%20no%20people%2C%20refined%20atmosphere&width=400&height=200&seq=fgt-corporate-premium&orientation=landscape"
                    alt="Soluções Corporativas"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent group-hover:from-black/30 transition-all duration-500"></div>
                </div>
                <div className="p-8">
                  <p className="text-[11px] uppercase tracking-[0.08em] font-medium text-[#F97700] mb-3">
                    {t('home:solutions.corporate.category')}
                  </p>
                  <h3 className="text-xl font-semibold text-[#0B0B0B] mb-3">
                    {t('home:solutions.corporate.title')}
                  </h3>
                  <p className="text-[14px] text-[#2E2E2E] leading-relaxed mb-4">
                    {t('home:solutions.corporate.description')}
                  </p>
                  <span className="text-sm font-medium text-[#0B0B0B] group-hover:text-[#F97700] transition-colors">
                    Saiba mais →
                  </span>
                </div>
              </Link>

              {/* Contemplated Letters with Premium Car */}
              <Link
                to="/cartas-contempladas"
                className="bg-white border border-[#E7E1D9] rounded-lg overflow-hidden hover:border-[#F97700] transition-all duration-500 group cursor-pointer hover:shadow-xl hover:scale-[1.02]"
              >
                <div className="relative h-32 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=premium%20luxury%20car%20Mercedes%20S-Class%20in%20sophisticated%20showroom%20with%20ambient%20lighting%2C%20high-end%20automotive%20retail%20environment%2C%20polished%20surfaces%20and%20elegant%20presentation%2C%20professional%20car%20photography%2C%20no%20people%2C%20refined%20and%20exclusive%20atmosphere&width=400&height=200&seq=fgt-letters-premium&orientation=landscape"
                    alt="Cartas Contempladas"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent group-hover:from-black/30 transition-all duration-500"></div>
                </div>
                <div className="p-8">
                  <p className="text-[11px] uppercase tracking-[0.08em] font-medium text-[#F97700] mb-3">
                    {t('home:solutions.letters.category')}
                  </p>
                  <h3 className="text-xl font-semibold text-[#0B0B0B] mb-3">
                    {t('home:solutions.letters.title')}
                  </h3>
                  <p className="text-[14px] text-[#2E2E2E] leading-relaxed mb-4">
                    {t('home:solutions.letters.description')}
                  </p>
                  <span className="text-sm font-medium text-[#0B0B0B] group-hover:text-[#F97700] transition-colors">
                    Saiba mais →
                  </span>
                </div>
              </Link>
            </div>
          </div>
        </section>

        {/* ── INTENT PATHWAYS: Elegant entry points ── */}
        <IntentPathwaysSection />

        {/* Premium CTA Banner with Interactive Background */}
        <section className="relative bg-[#0B0B0B] py-0 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=ultra%20close-up%20of%20premium%20Swiss%20watch%20movement%20with%20intricate%20golden%20gears%20and%20precision%20mechanics%2C%20luxury%20timepiece%20craftsmanship%20with%20warm%20amber%20lighting%20on%20dark%20obsidian%20background%2C%20macro%20photography%20with%20extreme%20detail%2C%20sophisticated%20engineering%20concept%2C%20no%20text%2C%20cinematic%20depth&width=1920&height=600&seq=fgt-banner-premium-v3&orientation=landscape"
              alt=""
              className="w-full h-full object-cover object-center transition-transform duration-1000 hover:scale-105"
              style={{ transform: `translateY(${scrollY * 0.1}px)` }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0B0B0B] via-[#0B0B0B]/90 to-[#0B0B0B]/70"></div>
          </div>

          <div className="relative z-10 max-w-[1440px] mx-auto px-8 lg:px-16 py-20 lg:py-24">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
              <div className="flex-1 max-w-[620px]">
                <p className="text-[11px] uppercase tracking-[0.12em] font-medium text-[#F97700] mb-4">
                  {t('home:diagBanner.eyebrow')}
                </p>
                <h2 className="text-4xl lg:text-5xl font-semibold text-white leading-[1.15] tracking-tight mb-5">
                  {t('home:diagBanner.title')}
                </h2>
                <p className="text-[16px] text-[#C8C0B4] leading-relaxed mb-0">
                  {t('home:diagBanner.description')}
                </p>
              </div>

              <div className="flex-shrink-0 w-full lg:w-auto">
                <div className="bg-white/5 border border-white/10 rounded-lg p-8 lg:p-10 backdrop-blur-sm text-center lg:text-left min-w-[320px] hover:bg-white/10 transition-all duration-500">
                  <ul className="space-y-3 mb-8">
                    {[
                      t('home:diagBanner.item1'),
                      t('home:diagBanner.item2'),
                      t('home:diagBanner.item3'),
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-3 text-[14px] text-[#E7E1D9]">
                        <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                          <i className="ri-check-line text-[#F97700] text-base"></i>
                        </span>
                        {item}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/diagnostico"
                    className="block w-full px-8 py-4 bg-[#F97700] text-white text-base font-semibold rounded hover:bg-[#E56E00] transition-all duration-300 hover:scale-[1.02] hover:shadow-lg text-center whitespace-nowrap cursor-pointer"
                  >
                    {t('home:diagBanner.cta')}
                  </Link>
                  <p className="text-[12px] text-[#7A7268] mt-3 text-center">
                    {t('home:diagBanner.disclaimer')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[#F97700] to-transparent opacity-40"></div>
        </section>

        {/* Process Section */}
        <section className="bg-[#F6F2EC] py-32">
          <div className="max-w-[1440px] mx-auto px-8 lg:px-16">
            <h2 className="text-4xl font-semibold text-[#0B0B0B] text-center mb-20">
              {t('home:process.title')}
            </h2>

            <div className="max-w-[1000px] mx-auto relative">
              <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-[#E7E1D9] hidden lg:block"></div>

              <div className="space-y-20">
                {[1, 2, 3, 4].map((step, index) => (
                  <div
                    key={step}
                    className={`flex flex-col lg:flex-row items-center gap-8 fade-on-scroll opacity-0 ${
                      index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                    }`}
                  >
                    <div className={`flex-1 ${index % 2 === 0 ? 'lg:text-right' : 'lg:text-left'}`}>
                      <h3 className="text-3xl font-semibold text-[#0B0B0B] mb-4">
                        {t(`home:process.step${step}.title`)}
                      </h3>
                      <p className="text-base text-[#2E2E2E] leading-relaxed max-w-[420px] mx-auto lg:mx-0">
                        {t(`home:process.step${step}.description`)}
                      </p>
                    </div>

                    <div className="w-16 h-16 rounded-full border-3 border-[#F97700] flex items-center justify-center bg-[#F6F2EC] z-10 flex-shrink-0">
                      <span className="text-2xl font-semibold text-[#F97700]">0{step}</span>
                    </div>

                    <div className="flex-1"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── TESTIMONIALS SECTION ── */}
        <section className="bg-[#FBF8F3] py-32 overflow-hidden">
          <div className="max-w-[1440px] mx-auto px-8 lg:px-16">
            {/* Header */}
            <div className="text-center mb-16 fade-on-scroll opacity-0">
              <p className="text-[11px] uppercase tracking-[0.12em] font-medium text-[#F97700] mb-3">
                {t('home:testimonials.eyebrow')}
              </p>
              <h2 className="text-4xl font-semibold text-[#0B0B0B] mb-4">
                {t('home:testimonials.title')}
              </h2>
              <p className="text-[15px] text-[#5A5248] max-w-[520px] mx-auto leading-relaxed">
                {t('home:testimonials.subtitle')}
              </p>
            </div>

            {/* Featured testimonial (large) */}
            {Array.isArray(testimonials) && testimonials.length > 0 && (
              <div className="mb-8">
                <div className="bg-white border border-[#E7E1D9] rounded-lg p-10 lg:p-14 relative overflow-hidden fade-on-scroll opacity-0 hover:shadow-xl transition-all duration-500">
                  {/* Decorative quote mark */}
                  <span className="absolute top-6 right-10 text-[120px] font-serif leading-none text-[#FFF1E6] select-none pointer-events-none">
                    &ldquo;
                  </span>

                  <div className="flex flex-col lg:flex-row gap-10 items-start relative z-10">
                    {/* Left: quote */}
                    <div className="flex-1">
                      <p className="text-[18px] lg:text-[20px] text-[#0B0B0B] leading-[1.7] font-light italic mb-8">
                        &ldquo;{testimonials[activeTestimonial]?.quote}&rdquo;
                      </p>
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-[#FFF1E6] border border-[#F97700]/30 flex items-center justify-center flex-shrink-0">
                          <i className="ri-user-line text-[#F97700] text-lg"></i>
                        </div>
                        <div>
                          <p className="text-[13px] font-semibold text-[#0B0B0B]">
                            {testimonials[activeTestimonial]?.profile}
                          </p>
                          <span
                            className="inline-block mt-1 text-[11px] font-medium px-2.5 py-0.5 rounded-full"
                            style={{
                              backgroundColor: `${tagColors[testimonials[activeTestimonial]?.tag] ?? '#F97700'}18`,
                              color: tagColors[testimonials[activeTestimonial]?.tag] ?? '#F97700',
                            }}
                          >
                            {testimonials[activeTestimonial]?.tag}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Right: credit highlight */}
                    <div className="flex-shrink-0 lg:w-[200px] bg-[#F6F2EC] rounded-lg p-6 text-center border border-[#E7E1D9]">
                      <p className="text-[11px] uppercase tracking-[0.08em] text-[#7A7268] mb-2">Crédito estruturado</p>
                      <p className="text-3xl font-semibold text-[#F97700]">
                        {testimonials[activeTestimonial]?.credit}
                      </p>
                    </div>
                  </div>

                  {/* Navigation dots */}
                  <div className="flex items-center gap-2 mt-8">
                    {testimonials.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setActiveTestimonial(i)}
                        className={`transition-all duration-300 rounded-full cursor-pointer hover:scale-110 ${
                          i === activeTestimonial
                            ? 'w-6 h-2 bg-[#F97700]'
                            : 'w-2 h-2 bg-[#D9D3CB] hover:bg-[#F97700]/50'
                        }`}
                        aria-label={`Depoimento ${i + 1}`}
                      />
                    ))}
                    <div className="ml-auto flex gap-2">
                      <button
                        onClick={() => setActiveTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length)}
                        className="w-9 h-9 flex items-center justify-center rounded-full border border-[#E7E1D9] hover:border-[#F97700] hover:text-[#F97700] hover:scale-110 transition-all duration-300 cursor-pointer text-[#2E2E2E]"
                        aria-label="Anterior"
                      >
                        <i className="ri-arrow-left-s-line text-lg"></i>
                      </button>
                      <button
                        onClick={() => setActiveTestimonial((prev) => (prev + 1) % testimonials.length)}
                        className="w-9 h-9 flex items-center justify-center rounded-full border border-[#E7E1D9] hover:border-[#F97700] hover:text-[#F97700] hover:scale-110 transition-all duration-300 cursor-pointer text-[#2E2E2E]"
                        aria-label="Próximo"
                      >
                        <i className="ri-arrow-right-s-line text-lg"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Grid of mini testimonials */}
            {Array.isArray(testimonials) && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {testimonials.map((item, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveTestimonial(i)}
                    className={`text-left bg-white border rounded-lg p-6 transition-all duration-300 cursor-pointer group hover:scale-[1.02] ${
                      i === activeTestimonial
                        ? 'border-[#F97700] shadow-md'
                        : 'border-[#E7E1D9] hover:border-[#F97700]/50 hover:shadow-lg'
                    }`}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-8 h-8 rounded-full bg-[#FFF1E6] flex items-center justify-center flex-shrink-0">
                        <i className="ri-user-line text-[#F97700] text-sm"></i>
                      </div>
                      <div className="min-w-0">
                        <p className="text-[12px] font-semibold text-[#0B0B0B] truncate">{item.profile}</p>
                        <span
                          className="inline-block mt-0.5 text-[10px] font-medium px-2 py-0.5 rounded-full truncate max-w-full"
                          style={{
                            backgroundColor: `${tagColors[item.tag] ?? '#F97700'}18`,
                            color: tagColors[item.tag] ?? '#F97700',
                          }}
                        >
                          {item.tag}
                        </span>
                      </div>
                    </div>
                    <p className="text-[13px] text-[#5A5248] leading-relaxed line-clamp-3 italic">
                      &ldquo;{item.quote}&rdquo;
                    </p>
                    <p className="text-[12px] font-semibold text-[#F97700] mt-3">{item.credit}</p>
                  </button>
                ))}
              </div>
            )}

            {/* Disclaimer */}
            <p className="text-center text-[12px] text-[#9A9288] mt-8 max-w-[600px] mx-auto leading-relaxed">
              <i className="ri-lock-line mr-1"></i>
              {t('home:testimonials.disclaimer')}
            </p>
          </div>
        </section>

        {/* Governance Block */}
        <section className="bg-[#F6F2EC] py-32">
          <div className="max-w-[1440px] mx-auto px-8 lg:px-16">
            <div className="max-w-[960px] mx-auto bg-[#FFF1E6] border-l-4 border-[#F97700] rounded p-8 lg:p-10 hover:shadow-lg transition-all duration-300">
              <div className="flex gap-6">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                  <i className="ri-information-line text-2xl text-[#F97700]"></i>
                </div>
                <p className="text-[15px] text-[#0B0B0B] leading-relaxed">
                  {t('home:governance.text')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── COMPARATIVE: Educational comparison table ── */}
        <ComparativeSection />

        {/* Final CTA */}
        <section className="bg-[#0B0B0B] py-32">
          <div className="max-w-[1440px] mx-auto px-8 lg:px-16 text-center">
            <div className="max-w-[720px] mx-auto">
              <h2 className="text-5xl font-semibold text-white mb-4">
                {t('home:finalCta.title')}
              </h2>
              <p className="text-lg text-[#E7E1D9] mb-10">
                {t('home:finalCta.description')}
              </p>
              <Link
                to="/diagnostico"
                className="inline-block px-12 py-5 bg-[#F97700] text-white text-lg font-medium rounded hover:bg-[#E56E00] transition-all duration-300 hover:scale-[1.02] hover:shadow-lg whitespace-nowrap cursor-pointer"
              >
                {t('common:cta.primary')}
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
